import { Box, Button, Dialog, IconButton, Stack, Typography, useMediaQuery } from '@mui/material'
import { useEffect } from 'react'
import { YALO_PALETTE, YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import type { Story } from '../demo'

interface Props {
  stories: readonly Story[]
  /** Index of the open story, or `null` when the viewer is closed. */
  index: number | null
  onIndexChange: (index: number) => void
  onClose: () => void
  onAction: (story: Story) => void
  /** Milliseconds before the reel advances on its own. `0` turns it off. */
  autoAdvanceMs?: number
  /** Shown in the header, as the design shows the distributor's name. */
  sourceName: string
}

const TITLE_ID = 'story-viewer-title'

/**
 * The full-screen reel (reference.html:1257–1293).
 *
 * It is a MUI `Dialog`, not a positioned `<div>`. Everything the brief requires
 * of an overlay — focus trapped inside it, Escape closing it, focus returned to
 * the tile that opened it — is behaviour `Dialog` already implements correctly
 * against the ARIA practices; hand-rolling it would be a second, worse
 * implementation of the same three rules.
 *
 * The design advances the reel by scroll-snapping a vertical list. Scroll snap
 * is a mouse-and-thumb gesture with no keyboard equivalent, so the same
 * movement is offered as real "Anterior"/"Siguiente" buttons and as the arrow
 * keys, and `autoAdvanceMs` drives it on its own.
 *
 * Auto-advance is off when the reader asked for reduced motion — an overlay
 * that replaces itself every few seconds is exactly the kind of unrequested
 * movement that setting exists to stop — and a test turns it off by passing 0
 * rather than by waiting on a real timer.
 */
export const StoryViewer = ({
  stories,
  index,
  onIndexChange,
  onClose,
  onAction,
  autoAdvanceMs = 5000,
  sourceName,
}: Props) => {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const story = index === null ? undefined : stories[index]
  const hasNext = index !== null && index < stories.length - 1
  const hasPrevious = index !== null && index > 0

  useEffect(() => {
    if (index === null || !hasNext || autoAdvanceMs <= 0 || reducedMotion) return
    const timer = setTimeout(() => onIndexChange(index + 1), autoAdvanceMs)
    return () => clearTimeout(timer)
  }, [index, hasNext, autoAdvanceMs, reducedMotion, onIndexChange])

  if (index === null || !story) return null

  return (
    <Dialog
      open
      fullScreen
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      PaperProps={{
        sx: { bgcolor: YALO_PALETTE.neutral100, color: YALO_SEMANTIC.fgOnDark1 },
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          if (hasNext) onIndexChange(index + 1)
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          if (hasPrevious) onIndexChange(index - 1)
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(120% 80% at 80% 0%, rgba(0,194,238,0.18) 0%, rgba(215,93,255,0.12) 40%, rgba(6,9,11,0) 75%)',
        }}
        aria-hidden
      />

      <Stack sx={{ position: 'relative', zIndex: 2, height: '100%', p: 0 }}>
        <Stack direction="row" spacing={0.625} sx={{ px: 2, pt: 3 }} aria-hidden>
          {stories.map((entry, position) => (
            <Box
              key={entry.key}
              sx={{
                flex: 1,
                height: 3,
                borderRadius: '2px',
                bgcolor:
                  position <= index ? YALO_SEMANTIC.fgOnDark1 : 'rgba(255,255,255,0.22)',
              }}
            />
          ))}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.75 }}>
          <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{sourceName}</Typography>
          <IconButton
            onClick={onClose}
            aria-label="Cerrar historias"
            sx={{
              width: 34,
              height: 34,
              bgcolor: 'rgba(255,255,255,0.12)',
              color: YALO_SEMANTIC.fgOnDark1,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 24 24"
              width={16}
              height={16}
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </Box>
          </IconButton>
        </Stack>

        <Stack spacing={1.75} sx={{ flex: 1, justifyContent: 'center', px: 3.5 }}>
          <Typography variant="overline" sx={{ color: YALO_SEMANTIC.fgOnDark3 }}>
            {story.eyebrow}
          </Typography>
          <Typography
            id={TITLE_ID}
            component="h2"
            sx={{ fontSize: 80, lineHeight: '82px', fontWeight: 800, letterSpacing: '-3px' }}
          >
            {story.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 19,
              lineHeight: '27px',
              fontWeight: 500,
              color: YALO_SEMANTIC.fgOnDark2,
              maxWidth: 280,
            }}
          >
            {story.body}
          </Typography>
        </Stack>

        <Stack spacing={1.5} sx={{ px: 3, pb: 3.75 }}>
          <Button
            fullWidth
            onClick={() => onAction(story)}
            sx={{
              height: 50,
              borderRadius: `${YALO_RADIUS.pill}px`,
              bgcolor: YALO_SEMANTIC.fgOnDark1,
              color: YALO_PALETTE.black,
              fontWeight: 600,
              fontSize: 15,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' },
            }}
          >
            {story.ctaLabel}
          </Button>

          <Stack direction="row" spacing={1} justifyContent="center">
            {hasPrevious && (
              <Button
                onClick={() => onIndexChange(index - 1)}
                sx={{ color: YALO_SEMANTIC.fgOnDark2, fontSize: 12.5 }}
              >
                Anterior
              </Button>
            )}
            {hasNext && (
              <Button
                onClick={() => onIndexChange(index + 1)}
                sx={{ color: YALO_SEMANTIC.fgOnDark2, fontSize: 12.5 }}
              >
                Siguiente
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  )
}
